import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import FunctionalRoleSelector from "admin/components/user-management/FunctionalRoleSelector";
import GroupSelector from "admin/components/user-management/GroupSelector";
import useUsers from "admin/hooks/useUsers";
import Role from "admin/types/interfaces/role";
import { UserGroup } from "admin/types/interfaces/user";
import { Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import useToast from "~/components/ui/toast";
import { extractErrorMessage } from "~/utils/apiClient";
import { trackEvent } from "~/utils/monitoring";

const { VITE_ACCESS_CONTROLLER_API } = import.meta.env;

type CreateUserTab = "roles" | "groups";

interface CreateUserOptions {
  email: string;
  full_name: string;
  roles: string[];
  tags: string[];
}

interface CreateUserFormData {
  email: string;
  first_name: string;
  last_name: string;
  roles: Role[];
  tags: UserGroup[];
  send_invite: boolean;
}

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateUserModal({ isOpen, onClose }: CreateUserModalProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState<CreateUserTab>("roles");
  const { mutate } = useUsers();
  const { showSuccess, showError } = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<CreateUserFormData>({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      roles: [],
      tags: [],
      send_invite: true,
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        first_name: "",
        last_name: "",
        email: "",
        roles: [],
        tags: [],
        send_invite: true,
      });
      setActiveTab("roles");
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: CreateUserFormData) => {
    const { first_name, last_name, send_invite, ...rest } = data;
    const url = `${VITE_ACCESS_CONTROLLER_API}/users?send_invite=${send_invite.toString()}`;
    setIsCreating(true);

    const apiPayload: CreateUserOptions = {
      ...rest,
      full_name: `${first_name} ${last_name}`,
      roles: data.roles.map((r) => r.id),
      tags: data.tags.map((t) => t.name),
    };
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(apiPayload),
    });

    setIsCreating(false);
    if (!response.ok) {
      if (response.status === 409) {
        showError("A user with that email already exists");
      } else {
        const body = await response.json().catch(() => null);
        showError(extractErrorMessage(body) ?? "Failed to create user, please try again");
      }
    } else {
      showSuccess("User successfully created");
      mutate();
      handleClose();
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-6xl max-h-[90vh] bg-background flex flex-col gap-0 p-0"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>New User</DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0 mt-4">
          <form className="flex flex-col flex-1 min-h-0">
            <div className="px-6 pb-4 shrink-0 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="gap-0">
                    First Name<span className="text-destructive">*</span>
                  </Label>
                  <Controller
                    name="first_name"
                    control={control}
                    rules={{
                      required: "First name is required",
                      pattern: {
                        value: /^[^0-9]+$/,
                        message: "First name cannot contain numbers",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        data-1p-ignore
                        id="first_name"
                        className={`border border-border bg-background ${
                          errors.first_name ? "border-destructive" : ""
                        }`}
                        placeholder="Enter first name"
                      />
                    )}
                  />
                  {errors.first_name && (
                    <p className="text-sm text-destructive">{errors.first_name.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="gap-0">
                    Last Name <span className="text-destructive">*</span>
                  </Label>
                  <Controller
                    name="last_name"
                    control={control}
                    rules={{
                      required: "Last name is required",
                      pattern: {
                        value: /^[^0-9]+$/,
                        message: "Last name cannot contain numbers",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        data-1p-ignore
                        id="last_name"
                        className={`border border-border bg-background ${
                          errors.last_name ? "border-destructive" : ""
                        }`}
                        placeholder="Enter last name"
                      />
                    )}
                  />
                  {errors.last_name && (
                    <p className="text-sm text-destructive">{errors.last_name.message}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="gap-0">
                  Email Address <span className="text-destructive">*</span>
                </Label>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Please enter a valid email address",
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      data-1p-ignore
                      id="email"
                      type="email"
                      className={`border border-border bg-background ${
                        errors.email ? "border-destructive" : ""
                      }`}
                      placeholder="Enter email address"
                    />
                  )}
                />
                {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
                <div className="flex items-center space-x-2 pt-1">
                  <Controller
                    name="send_invite"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="send_invite"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <Label className="text-sm font-normal cursor-pointer">
                    Email instructions to sign in
                  </Label>
                </div>
              </div>
            </div>

            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as CreateUserTab)}
              className="flex flex-col flex-1 min-h-0"
            >
              <div className="flex justify-start shrink-0 px-6 pt-2">
                <TabsList className="w-fit">
                  <TabsTrigger value="roles">Functional Roles</TabsTrigger>
                  <TabsTrigger value="groups">Groups</TabsTrigger>
                </TabsList>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto pb-6">
                <TabsContent value="roles" className="mt-2 px-6">
                  <Controller
                    name="roles"
                    control={control}
                    render={({ field }) => (
                      <FunctionalRoleSelector
                        selectedRoles={field.value}
                        setSelectedRoles={field.onChange}
                      />
                    )}
                  />
                  {errors.roles && (
                    <p className="text-sm text-destructive">{errors.roles.message}</p>
                  )}
                </TabsContent>
                <TabsContent value="groups" className="mt-2 px-6">
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field }) => (
                      <GroupSelector
                        selectedGroups={field.value}
                        setSelectedGroups={field.onChange}
                      />
                    )}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </form>
        </div>

        <div className="flex justify-end gap-4 px-6 py-4 border-t border-border">
          <Button type="button" variant="outline" onClick={handleClose} size="lg">
            Cancel
          </Button>
          <Button
            type="submit"
            size="lg"
            disabled={!isValid || isCreating}
            className="min-w-[96px]"
            onClick={() => {
              trackEvent("admin_user_create_clicked");
              handleSubmit(onSubmit)();
            }}
          >
            {isCreating ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
